import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { GoInfo } from "react-icons/go";
import toast from "react-hot-toast";
import { DateTime } from "luxon";
import Tooltip from "@mui/joy/Tooltip";
import { ApiUpdateEntity } from "@/legacy/ApiCallerOld";
import { HeaderTabEntities } from "@/Components/Header/GlobalTab";
import { useCache } from "@/Hooks/useCache";
import api from "@/Api/Api";
import { useAgent } from "@/Hooks/useAgent";
import OtherFunctions from "../../../Helpers/OtherFunctions";
import { globalTabRemoved } from "../../../Store/Reducers/globalTabsSlice";
import { TicketHeader } from "./Ticket/TicketHeader";
import { TicketSlaSection } from "./Ticket/TicketSlaSection";
import { TicketDetails } from "./Ticket/TicketDetails";
import { TicketNewAction } from "./Ticket/TicketNewAction";
import { TicketActions } from "./Ticket/TicketActions";
import { ActionButtons } from "./Ticket/ActionButtons";
import { getNewUrlAfterTabClose } from "../../../Components/Header/HeaderTabs";
import { TicketHistorySection } from "./Ticket/TicketHistory";
import { useAppDispatch, useAppSelector } from "../../../Store/hooks";
import { getImageUploadHandler } from "../../../Components/FormComponents/HtmlEditor/imageUploadHandler";
import Keys from "@shared/Components/Key/Keys";
import Key from "@shared/Components/Key/Key";
import { PreventWindowClose } from "@shared/lib/PreventWindowClose";
import { AlertBox } from "@shared/Components/AlertBox/AlertBox";
import { FormErrorCode, GD_SUPPORT_AGENT_ID, TicketStatuses } from "@shared/Enums/Enums";
import { Ticket, Action } from "@shared/Entities/EntityTypes";
import { InfoToast } from "@shared/Components/Utils/Toasts";
import HtmlEditor from "@shared/Components/FormComponents/HtmlEditor/HtmlEditor";
import TextInput from "@shared/Components/FormComponents/TextInput/TextInput";
import { Entities } from "@shared/Entities/Entities";
import { Hotkeys } from "@shared/lib/Hotkeys";
import { FormHotkeys } from "@shared/Components/Utils/FormHotkeys";

interface TicketViewerProps {
	id: number;
	data?: Ticket;
	createNew(data: Ticket): Promise<void>;
	updateData(data: Ticket): Promise<void>;
	refreshData(): void; // This is needed as the TicketViewer is also responsible for Actions.
}

function TicketViewer(props: TicketViewerProps) {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const cache = useCache();

	const [firstActionNote, setFirstActionNote] = useState<string>("");
	const [addingNewAction, setAddingNewAction] = useState(false);
	const [newActionIsPublic, setNewActionIsPublic] = useState(false);
	const [data, setData] = useState<Ticket | undefined>();
	const [attemptedToSetRead, setAttemptedToSetRead] = useState(false);
	const [noUserModal, setNoUserModal] = useState(false);

	const agent = useAgent();
	const tabsState = useAppSelector(state => state.globalTabs);

	useEffect(() => {
		// Override internal data when data changes in props.
		setData(props.data);

		setTicketRead();
	}, [props.data]);

	// Disabled for now. This causes strange behaviour where the 'add new user' modal closes on refresh.
	/*
	// Fetch every 30 secs.
	useEffect(() => {
		const interval = setInterval(() => {
			props.refreshData()
		}, 30000);

		return () => clearInterval(interval); // Clean up.
	}, [props.refreshData]); // Only run on mount.
	*/

	function setTicketRead() {
		// Weird check to get around EntityViewer PUTing a user as a ticket when you switch between those screens.
		// Remove this when we get rid of EntityViewer.
		if (props.data == null || props.data.ticketread == null) {
			return;
		}

		if (props.data != null && props.data.ticketread != true && !attemptedToSetRead && agent?.id != GD_SUPPORT_AGENT_ID) {
			const dataCopy = Object.assign({}, props.data);
			const newData = OtherFunctions.SafeChangeProperty(dataCopy, "ticketread", true);

			// Auto update if id != -1
			if (props.id != -1) {
				props.updateData(newData);
			} else {
				// If this is a new ticket store the changes locally.
				setData(newData);
			}

			setAttemptedToSetRead(true); // So it doesn't get stuck in an infinite loop if there's an issue.
		}
	}

	async function createNewTicket(note?: string) {
		if (data != null) {
			const newTicket: Ticket = Object.assign({}, data);

			// Add the creation date.
			newTicket.customCreated = DateTime.now().toISO();

			const actionNote = note != null && typeof note == "string" ? note : firstActionNote;

			// Include first action.
			const firstAction: Action = {
				id: 0,
				ticketId: 0,
				noteBody: actionNote,
				customCreated: DateTime.now().toISO(),
				isPublic: true,
				agentId: agent?.id
			};

			const actionsArray = [...newTicket.actions ?? []];
			actionsArray.push(firstAction);
			newTicket.actions = actionsArray;

			// Post.
			await props.createNew(newTicket);

			// Clear first action state.
			setFirstActionNote("");
		}
	}

	function addReply() {
		if (data?.userId == null) {
			setNoUserModal(true);
			return;
		}

		setAddingNewAction(true);
		setNewActionIsPublic(true);
	}

	function addPrivateNote() {
		setAddingNewAction(true);
		setNewActionIsPublic(false);
	}

	function clearActionPassback() {
		setAddingNewAction(false);
	}

	async function addAction(action: Action) {
		const res = await api.createEntity<Action>(Entities.ACTION, action);

		if (res.successful) {
			setAddingNewAction(false);

			props.refreshData();
		} else if (res.formErrorCode == FormErrorCode.SUPPORT_AGENT_ACTION) {
			toast.error("Remote support agent cannot add an action on a client instance.");
		} else {
			toast.error("Could not send message: " + res.errorMsg);
		}
	}

	/**
	 * @deprecated This ruins TS! Use useForm to keep and change an obj in state.
	 */
	async function handleChangeInFormComponent(key: string, newValue: any) {
		const dataCopy = Object.assign({}, data);
		const newData = OtherFunctions.SafeChangeProperty(dataCopy, key, newValue);

		// Auto update if id != -1
		if (props.id != -1) {
			await props.updateData(newData);
		} else {
			// If this is a new ticket store the changes locally.
			setData(newData);
		}
	}

	async function closeTicket() {
		await handleChangeInFormComponent("statusId", TicketStatuses.CLOSED);

		toast.success("Ticket closed.");
		dispatch(globalTabRemoved({ id: props.id, entity: HeaderTabEntities.TICKETS }));
		navigate("/tickets");
	}

	async function updateAction(action: Action) {
		await ApiUpdateEntity(Entities.ACTION, action.id, action);

		props.refreshData();
	}

	async function deleteAction(id: number) {
		const res = await api.deleteEntity(Entities.ACTION, id);

		if (!res.successful) {
			toast.error("Could not delete private note.");
		}

		props.refreshData();
	}

	function cancelTicket() {
		dispatch(globalTabRemoved({ id: -1, entity: HeaderTabEntities.TICKETS }));
		navigate("/tickets");
	}

	async function deleteTicket() {
		let newData = Object.assign({}, props.data);
		newData = OtherFunctions.SafeChangeProperty(newData, "deleted", true);

		await props.updateData(newData);

		navigate(getNewUrlAfterTabClose(tabsState.tabs, { id: newData.id, entity: HeaderTabEntities.TICKETS, title: "" }));
		dispatch(globalTabRemoved({ id: newData.id, entity: HeaderTabEntities.TICKETS }));

		toast("Ticket deleted.", InfoToast);
	}

	async function undeleteTicket() {
		let newData = Object.assign({}, props.data);
		newData = OtherFunctions.SafeChangeProperty(newData, "deleted", false);

		await props.updateData(newData);

		toast("Ticket un-deleted.", InfoToast);
	}

	const newTicket =
	data != null ?
		<div className="flex h-full">
			<FormHotkeys onSubmit={createNewTicket} onCancel={cancelTicket} />

			<div className="Entity-ViewerLeftPanel overflow-auto border-r border-gray-300 shrink-0">
				<div className="text-center py-3"><GoInfo /> DETAILS</div>
				<TicketDetails ticket={data} handleSubmit={handleChangeInFormComponent} />
			</div>
			<div className="flex-grow h-full p-3">
				<div className="w-1/2">
					<TextInput
						label="Subject"
						dataname="description"
						handleSubmit={handleChangeInFormComponent}
						mandatory
						autoFocus
					/>
				</div>
				<br />

				<PreventWindowClose stopClose={() => firstActionNote != ""} />

				<HtmlEditor
					value={firstActionNote}
					label="Note"
					dataname="note"
					agentFeatures
					handleCancel={cancelTicket}
					handleChange={(_key: string, newValue: any) => setFirstActionNote(newValue)}
					handleSubmit={createNewTicket}
					cannedReplies={cache.CannedReplies}
					imageUploadHandler={getImageUploadHandler(cache.getTenantId())}
				/>
				<br />

				<Tooltip title={<Keys label="Cancel" keys={[<Key>Esc</Key>]} />}>
					<button onClick={cancelTicket} className="btn-grey mr-2">Cancel</button>
				</Tooltip>

				<Tooltip title={<Keys label="Create" keys={[<Key>Ctrl</Key>, <Key>Enter</Key>]} />}>
					<button className="btn-blue" onClick={ () => createNewTicket() }>Create</button>
				</Tooltip>
			</div>
			<div className="w-80 border-l border-gray-300 shrink-0">
				<div className="text-center py-3">ACTIONS</div>
				<div className="border-t border-gray-300 p-3">Available actions will appear here once the ticket is submitted!</div>
			</div>
		</div>
		:
		null;

	const mergedParentTicket = props.data?.mergedTickets;
	const mergedIntoTicket = data?.mergedIntoTicket;

	const mergedTicketLinks = mergedParentTicket?.map(mergedParentTicket =>
		<Link to={"/tickets/" + mergedParentTicket.id} className="underline ml-1">#{mergedParentTicket.id} {mergedParentTicket.description}</Link>
	);

	const mergedTicketParent = mergedParentTicket && mergedParentTicket.length > 0 &&
		<div className="bg-white pl-3 pr-3 pt-3 -mb-3">
			<AlertBox alertType="info">
				The following tickets have been merged into this:{mergedTicketLinks}
			</AlertBox>
		</div>;

	const mergedTicketChild = mergedIntoTicket &&
		<div className="bg-white pl-3 pr-3 pt-3 -mb-3">
			<AlertBox alertType="info">
				This ticket has been merged into: <Link to={"/tickets/" + mergedIntoTicket.id} className="underline">#{mergedIntoTicket.id} {mergedIntoTicket.description}</Link>
			</AlertBox>
		</div>;

	const existingTicket =
	data != null ?
		<div className="flex h-full w-full">
			<Hotkeys hotkeys="r" callback={addReply} />
			<Hotkeys hotkeys="p" callback={addPrivateNote} />

			<div className="w-[19rem] lg:w-[25rem] overflow-auto border-r border-gray-300 shrink-0">
				<div className="text-center py-3"><GoInfo /> DETAILS</div>
				<TicketDetails ticket={data} handleSubmit={handleChangeInFormComponent} />
			</div>
			<div className="flex-grow h-full flex flex-col overflow-x-auto">
				<TicketHeader ticket={data} handleSubmit={handleChangeInFormComponent} />
				<div className="overflow-y-auto grow flex flex-col">
					{mergedTicketParent}
					{mergedTicketChild}

					{addingNewAction &&
						<TicketNewAction
							ticket={data}
							isPublic={newActionIsPublic}
							clearActionPassback={clearActionPassback}
							addActionPassback={addAction}
						/>}

					<div className="bg-white p-3 flex-grow">
						<TicketActions ticket={data} updatePrivateNote={updateAction} deletePrivateNote={deleteAction} />
					</div>
				</div>
			</div>
			<div className="w-[19rem] lg:w-[25rem] overflow-auto border-l border-gray-300 shrink-0 flex flex-col">
				<div className="text-center py-3">ACTIONS</div>
				<ActionButtons
					ticket={data}
					addReply={addReply}
					addPrivateNote={addPrivateNote}
					closeTicket={closeTicket}
					deleteTicket={deleteTicket}
					undeleteTicket={undeleteTicket}
				/>
				<div className="border-t border-gray-300 text-center py-3">SLA DETAILS</div>
				<TicketSlaSection ticket={data} handleSubmit={handleChangeInFormComponent} />
				<div className="border-t border-gray-300 text-center py-3 uppercase">Ticket History</div>
				<TicketHistorySection ticketId={data.id} ticketHistory={data.ticketHistory} />
			</div>
		</div>
		:
		null; // TODO: Handle and display errors in another if. Probably pass error in props.

	return (
		props.id == -1 ? newTicket : existingTicket
	);
}

export default TicketViewer;
